/**
 * DemoUnlockPage (`/demos/:slug/unlock`)
 *
 * 5-digit code entry screen. The operator reads the code aloud, the
 * prospect / staff member types it in, and on success we drop a short
 * sessionStorage flag + redirect to `/demos/:slug` where the bespoke
 * demo renders. Wrong code shows a "try again" message — no lockout
 * (the content behind the gate is marketing, not sensitive data).
 */
import React, { useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/auth";
import { KeyRound, ArrowLeft, Loader2 } from "lucide-react";
import DemoTopBar from "@/components/DemoTopBar";

const DIGITS = 5;

export default function DemoUnlockPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [digits, setDigits] = useState(Array(DIGITS).fill(""));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inputs = useRef([]);

  const handleChange = (idx, val) => {
    const v = val.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[idx] = v;
    setDigits(next);
    setError("");
    if (v && idx < DIGITS - 1) inputs.current[idx + 1]?.focus();
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputs.current[idx - 1]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };

  const handlePaste = (e) => {
    const raw = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, DIGITS);
    if (!raw) return;
    e.preventDefault();
    const next = Array(DIGITS).fill("");
    for (let i = 0; i < raw.length; i++) next[i] = raw[i];
    setDigits(next);
    const focusIdx = Math.min(raw.length, DIGITS - 1);
    inputs.current[focusIdx]?.focus();
  };

  const submit = async () => {
    const code = digits.join("");
    if (code.length !== DIGITS) {
      setError("Enter all 5 digits.");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/demo-directory/verify`, { slug, code });
      if (data?.ok) {
        try { sessionStorage.setItem(`demo_unlock_${slug}`, "1"); } catch {}
        navigate(`/demos/${slug}`);
      } else {
        setError("Wrong code — try again.");
        setDigits(Array(DIGITS).fill(""));
        inputs.current[0]?.focus();
      }
    } catch {
      setError("Something went wrong. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="demo-unlock-page"
      className="min-h-screen bg-[#0A192F] text-slate-100 flex flex-col"
    >
      <DemoTopBar />
      <div className="flex-1 flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <Link
          to="/demos"
          data-testid="demo-unlock-back"
          className="inline-flex items-center gap-1.5 text-slate-400 hover:text-brand-cyan font-mono text-[11px] tracking-[0.18em] uppercase mb-6 transition"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back to directory
        </Link>

        <div className="rounded-2xl border border-white/10 bg-navy-900/60 backdrop-blur-md p-8">
          <div className="flex items-center gap-2 text-brand-cyan font-mono text-[11px] tracking-[0.24em] uppercase mb-3">
            <KeyRound className="w-3.5 h-3.5" /> // ACCESS CODE
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight">
            Enter the 5-digit code<br />
            <span className="text-slate-400 text-base font-normal">
              Your operator will read it to you.
            </span>
          </h2>

          <div
            data-testid="demo-unlock-input-row"
            className="mt-6 grid grid-cols-5 gap-3"
            onPaste={handlePaste}
          >
            {digits.map((d, i) => (
              <input
                key={i}
                ref={(el) => (inputs.current[i] = el)}
                data-testid={`demo-unlock-digit-${i}`}
                type="text"
                inputMode="numeric"
                autoFocus={i === 0}
                maxLength={1}
                value={d}
                onChange={(e) => handleChange(i, e.target.value)}
                onKeyDown={(e) => handleKeyDown(i, e)}
                className="aspect-square text-center text-3xl font-mono bg-navy-950 border border-white/10 focus:border-brand-cyan focus:outline-none focus:ring-2 focus:ring-brand-cyan/30 rounded-xl text-white caret-brand-cyan transition"
              />
            ))}
          </div>

          {error && (
            <div
              data-testid="demo-unlock-error"
              role="alert"
              className="mt-4 text-rose-400 font-mono text-sm"
            >
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="demo-unlock-submit"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-blue to-brand-cyan hover:from-brand-cyan hover:to-brand-emerald text-navy-950 font-mono text-sm font-semibold tracking-[0.14em] uppercase py-3 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Verifying…</> : "Unlock Demo"}
          </button>
        </div>
      </div>
      </div>
    </section>
  );
}
